/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import config from 'js/app/config';
import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import user from 'js/lib/user';

import { Button, Grid, Typography2 } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';
import type { EventData } from '@coursera/event-pulse/react';

import { useVerifiedSkillBadgeDataContext } from 'bundles/skills-common/shared/verified-skills/context/pathway-badge/VerifiedSkillsBadgeDataContext';

import _t from 'i18n!nls/skills-common';

type Props = {
  skillName: string;
  numberOfSkills?: number;
  trackingData?: EventData<'click_button'>;
};

const styles = {
  container: css`
    background: linear-gradient(
      135deg,
      var(--cds-color-emphasis-quaternary-background-xweak) 0%,
      var(--cds-color-emphasis-primary-background-xweak) 100%
    );
    border: 1px solid var(--cds-color-emphasis-quaternary-background-weak);
    border-radius: var(--cds-border-radius-200);
    padding: var(--cds-spacing-400);
  `,
  contentColumn: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 180px;
  `,
  headerSection: css`
    flex-shrink: 0;
  `,
  bodySection: css`
    flex: 1;
    display: flex;
    align-items: center;
  `,
  buttonSection: css`
    flex-shrink: 0;
  `,
  completedBadgeImage: css`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 100%;
    max-width: 157px;
    margin-left: var(--cds-spacing-100);
  `,
  badgeImage: css`
    position: relative;
    z-index: 2;
    width: 100%;
    height: auto;
    max-width: 157px;
    max-height: 157px;
  `,
  badgePlaceholder: css`
    width: 100%;
    max-width: 80px;
    height: auto;
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 60px;
    min-height: 60px;
  `,
  titleText: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  subtitleText: css`
    margin: var(--cds-spacing-100) 0;
  `,
  badgeContainer: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
  `,
};

const SkillsDialogAchievementEarned = ({ skillName, numberOfSkills, trackingData }: Props) => {
  const { badge, loading } = useVerifiedSkillBadgeDataContext();
  const track = useTracker();

  const badgeImageUrl = badge?.badgeTemplate?.imageUrl;
  const badgeTitle = badge?.badgeTemplate?.title;

  if (loading) {
    return null;
  }

  const onViewBadge = () => {
    if (badge?.issuedBadgeId) {
      track('click_button', {
        button: {
          name: 'verified_skills_progress_dialog_view_badge',
        },
        ...(trackingData?.skill && { skill: trackingData.skill }),
        ...(trackingData?.badge && { badge: trackingData.badge }),
      });

      window.open(
        `${config.url.base}account/accomplishments/badge/${badge.issuedBadgeId}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const userDetails = user.get();
  const firstName = userDetails?.id_verification?.first_name;
  const fullName = userDetails?.full_name;
  const learnerName = firstName || fullName;

  return (
    <div css={styles.container} data-testid="achievement-earned">
      <Grid container spacing={4}>
        <Grid item xs={8}>
          <div css={styles.contentColumn}>
            <div css={styles.headerSection}>
              <Typography2 variant="subtitleLarge" component="h3" css={styles.titleText}>
                {_t('Achievement earned')}
              </Typography2>
              <Typography2 variant="titleXSmall" component="p">
                {skillName}
              </Typography2>
            </div>

            <div css={styles.bodySection}>
              <Typography2 variant="bodyPrimary" component="p" css={styles.subtitleText}>
                {learnerName ? (
                  <FormattedMessage
                    message={_t(
                      '{learnerName} has demonstrated expertise in {numberOfSkills} skill {numberOfSkills, plural, =1 {goal} other {goals}} and developed job-ready capabilities.'
                    )}
                    numberOfSkills={numberOfSkills}
                    learnerName={learnerName}
                  />
                ) : (
                  <FormattedMessage
                    message={_t(
                      'Congratulations! You have demonstrated expertise in {numberOfSkills} skill {numberOfSkills, plural, =1 {goal} other {goals}} and developed job-ready capabilities.'
                    )}
                    numberOfSkills={numberOfSkills}
                  />
                )}
              </Typography2>
            </div>

            {badge?.issuedBadgeId && (
              <div css={styles.buttonSection}>
                <Button size="small" variant="primary" onClick={onViewBadge} data-testid="view-badge-button">
                  {_t('View certificate')}
                </Button>
              </div>
            )}
          </div>
        </Grid>

        <Grid item xs={4} css={styles.badgeContainer}>
          {badgeImageUrl ? (
            <div css={styles.completedBadgeImage}>
              <Imgix
                src={badgeImageUrl}
                alt={badgeTitle || _t('Skill badge')}
                css={styles.badgeImage}
                data-testid="badge-image"
              />
            </div>
          ) : (
            <div css={styles.badgePlaceholder} data-testid="badge-placeholder">
              <CheckIcon size="large" color="success" />
            </div>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default SkillsDialogAchievementEarned;
